import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { queryNodeExecuteTask } from '$/domains/node/nodeService';
import type { NodeExecuteTaskResult } from '$/domains/node/type';
import { useLanguageStore } from '$/store/languageStore';
import type { RoleGenHistoryItem } from './HistoryList';
import { buildRoleGenFormSnapshot, createInitialRoleGenValues } from './roleMaterialUtils';
import { ROLE_GEN_BUSINESS_TYPE, type RoleGenNodeExtra } from './roleGenerateTask';
import { buildThreeViewImages } from './useRoleGenSubmit';

interface UseRoleGenHistoryOptions {
  open: boolean;
  roomId: string;
}

interface RoleGenTaskData {
  task_id?: string;
  state?: string;
  node_list?: Array<{
    state?: string;
    node_extra?: RoleGenNodeExtra;
  }>;
  steps?: Array<{
    state?: string;
  }>;
  last_image_urls?: string[];
  initial_transferred_urls?: string[];
  created_at?: number;
  create_time?: number;
  create_time_ms?: number;
  current_time_ms?: number;
}

const ROLE_GEN_HISTORY_PAGE_SIZE = 20;
const ROLE_GEN_HISTORY_POLL_INTERVAL_MS = 3000;
const ROLE_GEN_HISTORY_POLL_MAX_COUNT = 120;

/** 延迟历史轮询间隔，避免业务类型历史查询和任务查询连续打满。 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** 从任务子节点内读取创建角色回显数据，历史接口约定 node_extra 不在 task 同级。 */
function getRoleGenNodeExtra(task: RoleGenTaskData): RoleGenNodeExtra | undefined {
  for (const node of task.node_list ?? []) {
    const nodeExtra = node.node_extra;
    if (nodeExtra?.role_gen) return nodeExtra;
  }
  return undefined;
}

/** 汇总历史列表与任务轮询响应中的全部状态，避免顶层 SUCCESS 掩盖子节点 PENDING。 */
function getRoleGenTaskStates(task: RoleGenTaskData): string[] {
  return [
    task.state,
    ...(task.node_list ?? []).map((node) => node.state),
    ...(task.steps ?? []).map((step) => step.state),
  ].filter((state): state is string => Boolean(state));
}

/** 任一任务层级返回 FAIL 时视为生成失败。 */
function isFailedRoleGenTask(task: RoleGenTaskData): boolean {
  return getRoleGenTaskStates(task).includes('FAIL');
}

/** 根据任务状态和图片结果判断是否仍需继续轮询。 */
function isUnfinishedRoleGenTask(task: RoleGenTaskData): boolean {
  if (task.last_image_urls?.length) return false;
  const taskStates = getRoleGenTaskStates(task);
  if (taskStates.includes('FAIL')) return false;
  return taskStates.length === 0 || taskStates.some((state) => state !== 'SUCCESS');
}

/** 将任务时间格式化为历史记录展示文案。 */
function formatRoleGenHistoryTime(timestamp: number, todayText: string): string {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  return `${todayText} ${hour}:${minute}`;
}

/** 将 nodeexecutequery 返回任务转换为创建角色历史列表项。 */
function buildRoleGenHistoryItems(result: NodeExecuteTaskResult, todayText: string): RoleGenHistoryItem[] {
  return Object.entries(result.tasks ?? {}).map(([entryTaskId, task]) => {
    const roleTask = task as RoleGenTaskData;
    const taskId = roleTask.task_id || entryTaskId;
    const nodeExtra = getRoleGenNodeExtra(roleTask);
    const formSnapshot = nodeExtra
      ? buildRoleGenFormSnapshot(nodeExtra)
      : createInitialRoleGenValues();
    const createdAt = roleTask.create_time_ms
      || roleTask.current_time_ms
      || roleTask.created_at
      || roleTask.create_time
      || result.current_time_ms
      || Date.now();

    return {
      taskId,
      createdAt,
      modelId: formSnapshot.modelId,
      images: buildThreeViewImages(
        roleTask.last_image_urls ?? [],
        roleTask.initial_transferred_urls ?? [],
      ),
      formSnapshot,
      displayName: nodeExtra?.role_gen.name,
      displayTime: formatRoleGenHistoryTime(createdAt, todayText),
      loading: isUnfinishedRoleGenTask(roleTask),
      failed: isFailedRoleGenTask(roleTask),
    };
  });
}

/** 合并历史轮询结果，保持原列表顺序并按 taskId 覆盖最新状态。 */
function mergeRoleGenHistoryItems(
  prevItems: RoleGenHistoryItem[],
  nextItems: RoleGenHistoryItem[],
): RoleGenHistoryItem[] {
  const itemMap = new Map(prevItems.map((item) => [item.taskId, item]));
  nextItems.forEach((item) => itemMap.set(item.taskId, item));
  return Array.from(itemMap.values());
}

/** 创建角色历史查询，打开弹框时按 business_type 拉取，并轮询未完成任务。 */
export default function useRoleGenHistory({
  open,
  roomId,
}: UseRoleGenHistoryOptions): {
    items: RoleGenHistoryItem[];
    initialized: boolean;
    refresh: () => Promise<void>;
  } {
  const todayText = useLanguageStore((state) => (
    state.languageStore.smartcatTranslationText((value) => value['3'])
  ));
  const [items, setItems] = useState<RoleGenHistoryItem[]>([]);
  const [initialized, setInitialized] = useState(false);
  const pollVersionRef = useRef(0);

  /** 按任务 id 轮询历史里的未完成角色，完成后更新对应历史项。 */
  const pollUnfinishedTasks = useCallback(async (taskIds: string[], version: number): Promise<void> => {
    let pendingTaskIds = taskIds;
    for (let count = 0; count < ROLE_GEN_HISTORY_POLL_MAX_COUNT && pendingTaskIds.length > 0; count += 1) {
      if (pollVersionRef.current !== version) return;
      // eslint-disable-next-line no-await-in-loop
      await sleep(ROLE_GEN_HISTORY_POLL_INTERVAL_MS);
      if (pollVersionRef.current !== version) return;

      try {
        // eslint-disable-next-line no-await-in-loop
        const result = await queryNodeExecuteTask(pendingTaskIds, roomId);
        const nextItems = buildRoleGenHistoryItems(result, todayText);
        setItems((prev) => mergeRoleGenHistoryItems(prev, nextItems));
        pendingTaskIds = nextItems.filter((item) => item.loading).map((item) => item.taskId);
      } catch {
        // 单次轮询失败时保留当前历史列表，下一次打开弹框会重新拉取业务历史。
        return;
      }
    }
  }, [roomId, todayText]);

  /** 按 gen_role 业务类型刷新已生成角色历史，业务类型查询不传具体 task_ids。 */
  const refresh = useCallback(async (): Promise<void> => {
    if (!open || !roomId) return;
    const version = pollVersionRef.current + 1;
    pollVersionRef.current = version;
    setInitialized(false);
    try {
      const result = await queryNodeExecuteTask(
        undefined,
        roomId,
        undefined,
        undefined,
        1,
        ROLE_GEN_HISTORY_PAGE_SIZE,
        ROLE_GEN_BUSINESS_TYPE,
        1,
      );
      if (pollVersionRef.current !== version) return;
      const nextItems = buildRoleGenHistoryItems(result, todayText);
      setItems(nextItems);
      const unfinishedTaskIds = nextItems.filter((item) => item.loading).map((item) => item.taskId);
      pollUnfinishedTasks(unfinishedTaskIds, version).catch(() => undefined);
    } catch {
      if (pollVersionRef.current === version) {
        setItems([]);
      }
    } finally {
      if (pollVersionRef.current === version) {
        setInitialized(true);
      }
    }
  }, [open, pollUnfinishedTasks, roomId, todayText]);

  useEffect(() => {
    if (!open) {
      pollVersionRef.current += 1;
      setInitialized(false);
      return;
    }
    refresh().catch(() => undefined);
  }, [open, refresh]);

  return {
    items, initialized, refresh,
  };
}
